import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui';
import { Input, Field, FieldLabel, FieldError, PhoneField } from '../../../components/ui';
import type { Region } from '@court-sync/shared';
import type { UserFormData } from './schemas';
import type { UserFormReturn } from './types';

export interface PublicBookingUserFormStepProps {
  form: UserFormReturn;
  onSubmit: (data: UserFormData) => void;
  isSubmitting: boolean;
  onBack?: () => void;
  /** When false, omits the in-form title row (use an outer Card title instead). */
  showFormHeading?: boolean;
  submitButtonLabel?: string;
  defaultCountrySource?: { country?: string | null; region?: Region | null };
}

export function PublicBookingUserFormStep({
  form,
  onSubmit,
  isSubmitting,
  onBack,
  showFormHeading = true,
  submitButtonLabel = 'Continue to Student Details',
  defaultCountrySource,
}: PublicBookingUserFormStepProps) {
  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {(showFormHeading || onBack) && (
        <div className="flex items-center gap-3 mb-2">
          {onBack && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground -ml-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          {showFormHeading && (
            <h3 className="text-lg font-medium text-foreground">Create Your Account</h3>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field data-invalid={!!form.formState.errors.firstName}>
          <FieldLabel htmlFor="firstName">First Name *</FieldLabel>
          <Input id="firstName" aria-invalid={!!form.formState.errors.firstName} {...form.register('firstName')} placeholder="Enter first name" />
          <FieldError>{form.formState.errors.firstName?.message}</FieldError>
        </Field>
        <Field data-invalid={!!form.formState.errors.lastName}>
          <FieldLabel htmlFor="lastName">Last Name *</FieldLabel>
          <Input id="lastName" aria-invalid={!!form.formState.errors.lastName} {...form.register('lastName')} placeholder="Enter last name" />
          <FieldError>{form.formState.errors.lastName?.message}</FieldError>
        </Field>
      </div>
      <Field data-invalid={!!form.formState.errors.email}>
        <FieldLabel htmlFor="email">Email Address *</FieldLabel>
        <Input id="email" type="email" aria-invalid={!!form.formState.errors.email} {...form.register('email')} placeholder="Enter email address" />
        <FieldError>{form.formState.errors.email?.message}</FieldError>
      </Field>
      <PhoneField
        control={form.control}
        name="phone"
        label="Phone Number"
        id="phone"
        purpose="academy_registration"
        defaultCountrySource={defaultCountrySource}
        placeholder="Phone number"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field data-invalid={!!form.formState.errors.password}>
          <FieldLabel htmlFor="password">Password *</FieldLabel>
          <Input id="password" type="password" aria-invalid={!!form.formState.errors.password} {...form.register('password')} placeholder="Enter password" />
          <FieldError>{form.formState.errors.password?.message}</FieldError>
        </Field>
        <Field data-invalid={!!form.formState.errors.confirmPassword}>
          <FieldLabel htmlFor="confirmPassword">Confirm Password *</FieldLabel>
          <Input id="confirmPassword" type="password" aria-invalid={!!form.formState.errors.confirmPassword} {...form.register('confirmPassword')} placeholder="Confirm password" />
          <FieldError>{form.formState.errors.confirmPassword?.message}</FieldError>
        </Field>
      </div>
      <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...form.register('termsAccepted')}
            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-foreground">
            I agree to the{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 underline hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Terms of Service
            </a>
            *
          </span>
        </label>
        {form.formState.errors.termsAccepted && (
          <p className="text-sm text-destructive">{form.formState.errors.termsAccepted.message}</p>
        )}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...form.register('privacyAccepted')}
            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-foreground">
            I agree to the{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 underline hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Privacy Policy
            </a>
            *
          </span>
        </label>
        {form.formState.errors.privacyAccepted && (
          <p className="text-sm text-destructive">{form.formState.errors.privacyAccepted.message}</p>
        )}
      </div>
      <Button type="submit" className="w-full" isLoading={isSubmitting}>
        {submitButtonLabel}
      </Button>
    </form>
  );
}
